import React, { useState } from 'react';
import { Search, FileText, File, Folder, Eye, Download, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { InputFileInfo } from '@/types/api';
import { useDownloadRawFile } from '@/hooks/useFileManagement';
import { useToast } from '@/hooks/use-toast';
import { useAPIClient } from '@/hooks/useAPIClient';

interface FileListProps {
  files: InputFileInfo[];
  modules: Record<string, {
    total: number;
    processed: number;
    file_types: string[];
  }>;
  onFileSelect: (filepath: string) => void;
  selectedFile: string | null;
  onDataChange?: () => void | Promise<void>;
}

export function FileList({ files, modules, onFileSelect, selectedFile, onDataChange }: FileListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedModule, setSelectedModule] = useState<string>('all');
  const [selectedFileType, setSelectedFileType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [fileToDelete, setFileToDelete] = useState<InputFileInfo | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const downloadRawFile = useDownloadRawFile();
  const apiClient = useAPIClient();
  const { toast } = useToast();

  // Get all unique file types
  const allFileTypes = Array.from(new Set(files.map(f => f.file_type)));

  // Filter files based on search and filters
  const filteredFiles = files
    .filter(file => {
      const matchesSearch = !searchQuery ||
        file.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
        file.module.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesModule = selectedModule === 'all' || file.module === selectedModule;
      const matchesFileType = selectedFileType === 'all' || file.file_type === selectedFileType;

      // Status filtering: processed, pending, or failed
      const matchesStatus = selectedStatus === 'all' ||
        (selectedStatus === 'processed' && file.is_processed) ||
        (selectedStatus === 'pending' && !file.is_processed && !file.is_failed) ||
        (selectedStatus === 'failed' && file.is_failed);

      return matchesSearch && matchesModule && matchesFileType && matchesStatus;
    })
    .sort((a, b) => a.filename.localeCompare(b.filename));

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'pdf':
        return <File className="w-4 h-4 text-red-600" />;
      case 'txt':
        return <FileText className="w-4 h-4 text-blue-600" />;
      case 'docx':
        return <File className="w-4 h-4 text-blue-800" />;
      case 'pptx':
        return <File className="w-4 h-4 text-orange-600" />;
      default:
        return <File className="w-4 h-4 text-gray-600" />;
    }
  };

  const handleDownload = async (e: React.MouseEvent, file: InputFileInfo) => {
    e.stopPropagation(); // Prevent file selection

    const success = await downloadRawFile(file.filepath, file.filename);

    if (success) {
      toast({
        title: "Download gestartet",
        description: file.filename,
      });
    } else {
      toast({
        title: "Download fehlgeschlagen",
        description: file.filename,
        variant: "destructive",
      });
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, file: InputFileInfo) => {
    e.stopPropagation(); // Prevent file selection
    setFileToDelete(file);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;

    setIsDeleting(true);
    try {
      await apiClient.files.deleteDocument(fileToDelete.filepath);

      toast({
        title: "Dokument gelöscht",
        description: fileToDelete.filename,
      });

      // Refresh the data without full page reload
      if (onDataChange) {
        await onDataChange();
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast({
        title: "Löschen fehlgeschlagen",
        description: fileToDelete.filename,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setFileToDelete(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search and Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Dateien suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex gap-2 sm:gap-4">
          <Select value={selectedModule} onValueChange={setSelectedModule}>
            <SelectTrigger className="w-32 sm:w-48">
              <SelectValue placeholder="Alle Module" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Module</SelectItem>
              {Object.keys(modules).map(module => (
                <SelectItem key={module} value={module}>
                  <div className="flex items-center gap-2">
                    <Folder className="w-4 h-4" />
                    {module} ({modules[module].total})
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedFileType} onValueChange={setSelectedFileType}>
            <SelectTrigger className="w-20 sm:w-28">
              <SelectValue placeholder="Alle Typen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Typen</SelectItem>
              {allFileTypes.map(type => (
                <SelectItem key={type} value={type}>
                  {type.toUpperCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-24 sm:w-32">
              <SelectValue placeholder="Alle Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="processed">Processed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>


      {/* Files List */}
      <div className="space-y-3">
        {filteredFiles.map((file) => (
          <Card 
            key={file.filepath} 
            className={`cursor-pointer transition-colors hover:bg-muted/50 ${
              selectedFile === file.filepath ? 'ring-2 ring-primary' : ''
            }`}
            onClick={() => onFileSelect(file.filepath)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {getFileIcon(file.file_type)}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate filename-display">{file.filename}</h3>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Folder className="w-3 h-3" />
                        {file.module}
                      </span>
                      <span>{formatFileSize(file.size_bytes)}</span>
                      <span>{formatDate(file.upload_date)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Badge variant={
                    file.is_failed ? "destructive" : 
                    file.is_processed ? "default" : "secondary"
                  }>
                    {file.is_failed ? "Failed" : file.is_processed ? "Processed" : "Pending"}
                  </Badge>
                  <Badge variant="outline">
                    {file.file_type.toUpperCase()}
                  </Badge>
                  <Button variant="ghost" size="sm" title="View file">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleDownload(e, file)}
                    title="Download raw file"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleDeleteClick(e, file)}
                    title="Delete document"
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {file.processed_files.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <div className="text-xs text-muted-foreground">
                    Processed outputs: {file.processed_files.join(', ')}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredFiles.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <File className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No files found matching your search criteria.</p>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!fileToDelete} onOpenChange={() => setFileToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{fileToDelete?.filename}</strong>?
              <br /><br />
              This will remove the document from:
              <ul className="list-disc ml-6 mt-2">
                <li>Input file storage</li>
                <li>Processed markdown output</li>
                <li>Vector database (VDB) search index</li>
                <li>All related metadata</li>
              </ul>
              <br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}